import { exportMappingsService } from '@/api/exportMappings';
import type {
  ExportFieldMapping,
  ExportFieldMappingCreate,
  ExportFieldMappingUpdate,
  ExportMapping,
  ExportMappingCreate,
  ExportMappingImport,
  ExportMappingUpdate,
  ExportPreview,
  MetamodelResponse
} from '@/api/types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/**
 * Store Pinia pour la gestion des export mappings
 */
export const useExportMappingsStore = defineStore('exportMappings', () => {
  // ===============================
  // État réactif
  // ===============================

  const mappings = ref<ExportMapping[]>([]);
  const templates = ref<ExportMapping[]>([]);
  const currentMapping = ref<ExportMapping | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Cache pour les métamodèles et aperçus
  const metamodelsCache = ref<Map<string, MetamodelResponse>>(new Map());
  const previewsCache = ref<Map<string, ExportPreview>>(new Map());
  
  // Cache universel pour le métamodèle (pas lié à un mapping spécifique)
  const universalMetamodel = ref<MetamodelResponse | null>(null);

  // ===============================
  // Getters (Propriétés calculées)
  // ===============================

  /**
   * Mappings actifs seulement
   */
  const activeMappings = computed(() => 
    mappings.value.filter(mapping => mapping.is_active)
  );

  /**
   * Mappings utilisateur (non templates)
   */
  const userMappings = computed(() => 
    mappings.value.filter(mapping => !mapping.is_template)
  );

  /**
   * Mappings groupés par format
   */
  const mappingsByFormat = computed(() => {
    const grouped: Record<string, ExportMapping[]> = {};
    mappings.value.forEach(mapping => {
      if (!grouped[mapping.format_type]) {
        grouped[mapping.format_type] = [];
      }
      grouped[mapping.format_type].push(mapping);
    });
    return grouped;
  });

  /**
   * Statistiques sur les mappings
   */
  const mappingsStats = computed(() => ({
    total: mappings.value.length,
    active: activeMappings.value.length,
    templates: templates.value.length,
    byFormat: {
      json: mappings.value.filter(m => m.format_type === 'json').length,
      csv: mappings.value.filter(m => m.format_type === 'csv').length,
      xml: mappings.value.filter(m => m.format_type === 'xml').length,
    }
  }));

  /**
   * Trouve un mapping par UUID
   */
  const getMappingByUuid = computed(() => {
    return (uuid: string): ExportMapping | undefined => 
      mappings.value.find(mapping => mapping.uuid === uuid);
  });

  // ===============================
  // Actions principales
  // ===============================

  /**
   * Charge les mappings actifs (visibles par tous les utilisateurs)
   */
  async function loadMappings(force = false): Promise<void> {
    if (!force && mappings.value.length > 0) {
      return; // Cache existe déjà
    }

    isLoading.value = true;
    error.value = null;

    try {
      mappings.value = await exportMappingsService.getActive();
    } catch (err) {
      error.value = `Erreur lors du chargement des mappings: ${err}`;
      console.error('Store loadMappings error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Charge les templates prédéfinis
   */
  async function loadTemplates(force = false): Promise<void> {
    if (!force && templates.value.length > 0) {
      return; // Cache existe déjà
    }

    isLoading.value = true;
    error.value = null;

    try {
      templates.value = await exportMappingsService.getTemplates();
    } catch (err) {
      error.value = `Erreur lors du chargement des templates: ${err}`;
      console.error('Store loadTemplates error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Charge un mapping spécifique
   */
  async function loadMapping(uuid: string, useCache = true): Promise<ExportMapping> {
    // Vérifier le cache d'abord
    if (useCache) {
      const cached = getMappingByUuid.value(uuid);
      if (cached) {
        currentMapping.value = cached;
        return cached;
      }
    }

    isLoading.value = true;
    error.value = null;

    try {
      const mapping = await exportMappingsService.getById(uuid);
      currentMapping.value = mapping;

      // Mettre à jour le cache
      const index = mappings.value.findIndex(m => m.uuid === uuid);
      if (index !== -1) {
        mappings.value[index] = mapping;
      } else {
        mappings.value.push(mapping);
      }

      return mapping;
    } catch (err) {
      error.value = `Erreur lors du chargement du mapping: ${err}`;
      console.error('Store loadMapping error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Crée un nouveau mapping
   */
  async function createMapping(mappingData: ExportMappingCreate): Promise<ExportMapping | null> {
    console.log('[STORE] Tentative de création de mapping avec:', mappingData);
    isLoading.value = true;
    error.value = null;

    // Validation des données
    const validationErrors = exportMappingsService.validateMapping(mappingData);
    if (validationErrors.length > 0) {
      error.value = validationErrors.join(', ');
      isLoading.value = false;
      throw new Error(error.value);
    }

    try {
      const newMapping = await exportMappingsService.create(mappingData);
      
      // Ajouter au cache
      mappings.value.push(newMapping);
      currentMapping.value = newMapping;

      console.log('[STORE] Mapping créé avec succès:', newMapping);
      return newMapping;
    } catch (err) {
      error.value = `Erreur lors de la création du mapping: ${err}`;
      console.error('[STORE] Erreur dans createMapping:', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Met à jour un mapping existant
   */
  async function updateMapping(uuid: string, mappingData: ExportMappingUpdate): Promise<ExportMapping | null> {
    console.log(`[STORE] Tentative de mise à jour du mapping ${uuid} avec:`, mappingData);
    isLoading.value = true;
    error.value = null;

    // Validation des données
    const validationErrors = exportMappingsService.validateMapping(mappingData);
    if (validationErrors.length > 0) {
      error.value = validationErrors.join(', ');
      isLoading.value = false;
      throw new Error(error.value);
    }

    try {
      const updatedMapping = await exportMappingsService.update(uuid, mappingData);
      
      // Mettre à jour le cache
      const index = mappings.value.findIndex(m => m.uuid === uuid);
      if (index !== -1) {
        mappings.value[index] = updatedMapping;
      }
      
      // Mettre à jour le mapping courant si nécessaire
      if (currentMapping.value?.uuid === uuid) {
        currentMapping.value = updatedMapping;
      }

      // Vider les caches liés à ce mapping
      metamodelsCache.value.delete(uuid);
      previewsCache.value.delete(uuid);

      console.log('[STORE] Mapping mis à jour avec succès:', updatedMapping);
      return updatedMapping;
    } catch (err) {
      error.value = `Erreur lors de la mise à jour du mapping: ${err}`;
      console.error(`[STORE] Erreur dans updateMapping pour UUID ${uuid}:`, err);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Supprime un mapping
   */
  async function deleteMapping(uuid: string): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      await exportMappingsService.delete(uuid);
      
      // Retirer du cache
      mappings.value = mappings.value.filter(m => m.uuid !== uuid);
      
      // Nettoyer le mapping courant si nécessaire
      if (currentMapping.value?.uuid === uuid) {
        currentMapping.value = null;
      }

      // Vider les caches liés à ce mapping
      metamodelsCache.value.delete(uuid);
      previewsCache.value.delete(uuid);

    } catch (err) {
      error.value = `Erreur lors de la suppression du mapping: ${err}`;
      console.error('Store deleteMapping error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Duplique un mapping
   */
  async function duplicateMapping(uuid: string): Promise<ExportMapping> {
    isLoading.value = true;
    error.value = null;

    try {
      const duplicatedMapping = await exportMappingsService.duplicate(uuid);
      
      // Ajouter au cache
      mappings.value.push(duplicatedMapping);

      return duplicatedMapping;
    } catch (err) {
      error.value = `Erreur lors de la duplication du mapping: ${err}`;
      console.error('Store duplicateMapping error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // ===============================
  // Actions spécialisées
  // ===============================

  /**
   * Récupère le métamodèle universel (structure des champs exportables)
   * NOUVEAU : Le métamodèle est universel et ne dépend pas d'un mapping spécifique.
   */
  async function getUniversalMetamodel(useCache = true): Promise<MetamodelResponse> {
    // Vérifier le cache universel
    if (useCache && universalMetamodel.value) {
      return universalMetamodel.value;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const metamodel = await exportMappingsService.getUniversalMetamodel();
      
      // Debug: Afficher le métamodèle pour vérifier la structure
      //console.log('🔍 Métamodèle universel chargé:', metamodel);
      
      // Mettre en cache
      universalMetamodel.value = metamodel;
      
      return metamodel;
    } catch (err) {
      error.value = `Erreur lors du chargement du métamodèle universel: ${err}`;
      console.error('Store getUniversalMetamodel error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Génère un aperçu d'export avec cache
   */
  async function getPreview(uuid: string, useCache = true): Promise<ExportPreview> {
    // Vérifier le cache
    if (useCache && previewsCache.value.has(uuid)) {
      return previewsCache.value.get(uuid)!;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const preview = await exportMappingsService.preview(uuid);
      
      // Mettre en cache
      previewsCache.value.set(uuid, preview);

      return preview;
    } catch (err) {
      error.value = `Erreur lors de la génération de l'aperçu: ${err}`;
      console.error('Store getPreview error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Télécharge la configuration d'un mapping
   */
  async function downloadConfig(uuid: string, filename?: string): Promise<void> {
    error.value = null;

    try {
      await exportMappingsService.downloadConfig(uuid, filename);
    } catch (err) {
      error.value = `Erreur lors du téléchargement: ${err}`;
      console.error('Store downloadConfig error:', err);
      throw err;
    }
  }

  /**
   * Importe une configuration de mapping
   */
  async function importConfig(importData: ExportMappingImport): Promise<ExportMapping> {
    isLoading.value = true;
    error.value = null;

    try {
      const importedMapping = await exportMappingsService.importConfig(importData);
      
      // Ajouter au cache
      mappings.value.push(importedMapping);

      return importedMapping;
    } catch (err) {
      error.value = `Erreur lors de l'import: ${err}`;
      console.error('Store importConfig error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }



  // ===============================
  // Gestion des Field Mappings
  // ===============================

  /**
   * Ajoute un field mapping à un mapping
   */
  async function addFieldMapping(
    mappingUuid: string, 
    fieldMappingData: ExportFieldMappingCreate
  ): Promise<ExportFieldMapping> {
    isLoading.value = true;
    error.value = null;

    try {
      const newFieldMapping = await exportMappingsService.createFieldMapping(mappingUuid, fieldMappingData);
      
      // Mettre à jour le cache
      const mapping = getMappingByUuid.value(mappingUuid);
      if (mapping) {
        mapping.field_mappings.push(newFieldMapping);
      }

      // Mettre à jour le mapping courant
      if (currentMapping.value?.uuid === mappingUuid) {
        currentMapping.value.field_mappings.push(newFieldMapping);
      }

      // Vider les caches liés à ce mapping
      metamodelsCache.value.delete(mappingUuid);
      previewsCache.value.delete(mappingUuid);

      return newFieldMapping;
    } catch (err) {
      error.value = `Erreur lors de l'ajout du field mapping: ${err}`;
      console.error('Store addFieldMapping error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Met à jour un field mapping
   */
  async function updateFieldMapping(
    mappingUuid: string,
    fieldMappingId: number,
    fieldMappingData: ExportFieldMappingUpdate
  ): Promise<ExportFieldMapping> {
    isLoading.value = true;
    error.value = null;

    try {
      const updatedFieldMapping = await exportMappingsService.updateFieldMapping(
        mappingUuid, 
        fieldMappingId, 
        fieldMappingData
      );
      
      // Mettre à jour le cache
      const mapping = getMappingByUuid.value(mappingUuid);
      if (mapping) {
        const index = mapping.field_mappings.findIndex(fm => fm.id === fieldMappingId);
        if (index !== -1) {
          mapping.field_mappings[index] = updatedFieldMapping;
        }
      }

      // Mettre à jour le mapping courant
      if (currentMapping.value?.uuid === mappingUuid) {
        const index = currentMapping.value.field_mappings.findIndex(fm => fm.id === fieldMappingId);
        if (index !== -1) {
          currentMapping.value.field_mappings[index] = updatedFieldMapping;
        }
      }

      // Vider les caches liés à ce mapping
      metamodelsCache.value.delete(mappingUuid);
      previewsCache.value.delete(mappingUuid);

      return updatedFieldMapping;
    } catch (err) {
      error.value = `Erreur lors de la mise à jour du field mapping: ${err}`;
      console.error('Store updateFieldMapping error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Supprime un field mapping
   */
  async function deleteFieldMapping(mappingUuid: string, fieldMappingId: number): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      await exportMappingsService.deleteFieldMapping(mappingUuid, fieldMappingId);
      
      // Mettre à jour le cache
      const mapping = getMappingByUuid.value(mappingUuid);
      if (mapping) {
        mapping.field_mappings = mapping.field_mappings.filter(fm => fm.id !== fieldMappingId);
      }

      // Mettre à jour le mapping courant
      if (currentMapping.value?.uuid === mappingUuid) {
        currentMapping.value.field_mappings = currentMapping.value.field_mappings.filter(
          fm => fm.id !== fieldMappingId
        );
      }

      // Vider les caches liés à ce mapping
      metamodelsCache.value.delete(mappingUuid);
      previewsCache.value.delete(mappingUuid);

    } catch (err) {
      error.value = `Erreur lors de la suppression du field mapping: ${err}`;
      console.error('Store deleteFieldMapping error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // ===============================
  // Utilitaires
  // ===============================

  /**
   * Remet à zéro l'état complet
   */
  function resetState(): void {
    mappings.value = [];
    templates.value = [];
    currentMapping.value = null;
    error.value = null;
    isLoading.value = false;
    metamodelsCache.value.clear();
    previewsCache.value.clear();
  }

  /**
   * Vide tous les caches
   */
  function clearCaches(): void {
    metamodelsCache.value.clear();
    previewsCache.value.clear();
    universalMetamodel.value = null;
  }

  /**
   * Efface les erreurs
   */
  function clearError(): void {
    error.value = null;
  }

  /**
   * Définit le mapping courant
   */
  function setCurrentMapping(mapping: ExportMapping | null): void {
    currentMapping.value = mapping;
  }

  return {
    // État
    mappings,
    templates,
    currentMapping,
    isLoading,
    error,

    // Getters
    activeMappings,
    userMappings,
    mappingsByFormat,
    mappingsStats,
    getMappingByUuid,

    // Actions principales
    loadMappings,
    loadTemplates,
    loadMapping,
    createMapping,
    updateMapping,
    deleteMapping,
    duplicateMapping,

    // Actions spécialisées
    getUniversalMetamodel,
    getPreview,
    downloadConfig,
    importConfig,

    // Field Mappings
    addFieldMapping,
    updateFieldMapping,
    deleteFieldMapping,

    // Utilitaires
    resetState,
    clearCaches,
    clearError,
    setCurrentMapping
  };
}); 